import { api } from '../../api/client';
import { useCursorPages } from '../../lib/useCursorPages';
import type { AgencyCommissionRow, AgencyPayoutRow, AgencyRechargeRow, AgencyUserRow, Page } from './agencyApi';

/**
 * Cursor-paged agency lists. `base` is `/admin/agencies/<id>` (admin) or
 * `/admin/agency-portal` (the agency itself — the API scopes it to the login).
 */
export function useAgencyUsers(base: string) {
  return useCursorPages<AgencyUserRow & { id: string }>(['admin', 'agency-lists', base, 'users'], async (cursor) => {
    const page = (await api.get<Page<AgencyUserRow>>(`${base}/users`, { params: { cursor, limit: 50 } })).data;
    return { items: page.items.map((r) => ({ ...r, id: r.userId })), nextCursor: page.nextCursor };
  });
}

export function useAgencyRecharges(base: string) {
  return useCursorPages<AgencyRechargeRow & { id: string }>(['admin', 'agency-lists', base, 'recharges'], async (cursor) => {
    const page = (await api.get<Page<AgencyRechargeRow>>(`${base}/recharges`, { params: { cursor, limit: 50 } })).data;
    return { items: page.items.map((r) => ({ ...r, id: r.orderId })), nextCursor: page.nextCursor };
  });
}

export function useAgencyCommissions(base: string) {
  return useCursorPages<AgencyCommissionRow>(['admin', 'agency-lists', base, 'commissions'], async (cursor) =>
    (await api.get<Page<AgencyCommissionRow>>(`${base}/commissions`, { params: { cursor, limit: 50 } })).data,
  );
}

/** Payouts of one agency (admin: `/admin/agency-payouts?agencyId=`; portal: `/admin/agency-portal/payouts`). */
export function useAgencyPayouts(url: string, params: Record<string, string | undefined> = {}) {
  return useCursorPages<AgencyPayoutRow>(['admin', 'agency-lists', url, params], async (cursor) =>
    (await api.get<Page<AgencyPayoutRow>>(url, { params: { ...params, cursor, limit: 50 } })).data,
  );
}
